import { toast } from "sonner";
import { useToastAction } from "@/context/toast-action-context";

const MULTIPLE_MODEL_WARNING_EVENT = "multiple-model-warning-toast-change";
const activeMultipleModelWarningToastIds = new Set<string>();

function emitMultipleModelWarningToastChange() {
  if (typeof globalThis.window === "undefined") return;

  globalThis.window.dispatchEvent(
    new CustomEvent(MULTIPLE_MODEL_WARNING_EVENT, {
      detail: { count: activeMultipleModelWarningToastIds.size },
    }),
  );
}

export function subscribeMultipleModelWarningToasts(
  callback: (count: number) => void,
) {
  if (typeof globalThis.window === "undefined") return () => undefined;

  const handler = (event: Event) => {
    const customEvent = event as CustomEvent<{ count: number }>;
    callback(customEvent.detail?.count ?? 0);
  };

  globalThis.window.addEventListener(MULTIPLE_MODEL_WARNING_EVENT, handler);
  callback(activeMultipleModelWarningToastIds.size);

  return () => globalThis.window.removeEventListener(MULTIPLE_MODEL_WARNING_EVENT, handler);
}

export function hasMultipleModelWarningToasts() {
  return activeMultipleModelWarningToastIds.size > 0;
}

type ActionConfig = {
  name: string;
  payload: any;
  getIsLoading?: () => boolean;
};

interface CustomWarningToastProps {
  t: string | number;
  toastId: string;
  message: string;
  actionConfig?: ActionConfig;
  dispatchToastAction: (name: string, payload: any) => void;
}

const CustomWarningToast: React.FC<CustomWarningToastProps> = ({
  t,
  toastId,
  message,
  actionConfig,
  dispatchToastAction,
}) => {
  return (
    <div
      onClick={() => {
        const isCurrentlyLoading = actionConfig?.getIsLoading
          ? actionConfig.getIsLoading()
          : false;
        if (actionConfig && !isCurrentlyLoading) {
          dispatchToastAction(actionConfig.name, actionConfig.payload);
          activeMultipleModelWarningToastIds.delete(toastId);
          emitMultipleModelWarningToastChange();
          toast.dismiss(t);
        }
      }}
      className={`relative flex items-center gap-2 px-4 py-3 bg-amber-50 text-amber-600 rounded-md w-full shadow-md border border-amber-100 ${
        actionConfig ? "cursor-pointer" : "cursor-default"
      }`}
    >
      {/* Invisible bridge */}
      <div className="absolute -top-5 -bottom-5 inset-x-0 -z-10 bg-transparent" />

      {/* CLOSE BUTTON */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          activeMultipleModelWarningToastIds.delete(toastId);
          emitMultipleModelWarningToastChange();
          toast.dismiss(t);
        }}
        className="absolute top-0 left-0 -translate-x-[35%] -translate-y-[35%] w-5 h-5 flex items-center justify-center border border-amber-100 rounded-full cursor-pointer p-0 shadow-sm z-10 bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors"
        aria-label="Close toast"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>

      {/* WARNING ICON */}
      <div className="flex items-center justify-center shrink-0">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          height="20"
          width="20"
        >
          <path
            fillRule="evenodd"
            d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z"
            clipRule="evenodd"
          ></path>
        </svg>
      </div>

      {/* TEXT */}
      <div className="flex-1 text-medium">{message}</div>
    </div>
  );
};

export function useMultipleModelWarningToast() {
  const { dispatchToastAction } = useToastAction();

  const showWarningToast = (
    toastKey: string,
    message: string,
    actionConfig?: { name: string; payload: any; getIsLoading?: () => boolean },
  ) => {
    const toastId = `multiple-model-warning-${toastKey}`;
    activeMultipleModelWarningToastIds.add(toastId);

    toast.custom(
      (t) => (
        <CustomWarningToast
          t={t}
          toastId={toastId}
          message={message}
          actionConfig={actionConfig}
          dispatchToastAction={dispatchToastAction}
        />
      ),
      {
        id: toastId,
        duration: Infinity,
        onDismiss: () => {
          activeMultipleModelWarningToastIds.delete(toastId);
          emitMultipleModelWarningToastChange();
        },
      },
    );

    emitMultipleModelWarningToastChange();
  };

  return { showWarningToast };
}

export function dismissAllMultipleModelWarningToasts() {
  const idsToDismiss = Array.from(activeMultipleModelWarningToastIds);
  for (const toastId of idsToDismiss) {
    toast.dismiss(toastId);
  }
  activeMultipleModelWarningToastIds.clear();
  emitMultipleModelWarningToastChange();
}